/**
 * Predictive search.
 *
 * Results are rendered by sections/predictive-search.liquid and fetched as HTML
 * via the Section Rendering API, so prices and markup come from Liquid rather
 * than being rebuilt here.
 *
 * An enhancement only: the header control stays a link to /search and the
 * dialog contains a real GET form, so Enter and no-JS both work.
 */
(() => {
  const SECTION = 'predictive-search';
  const MIN_LENGTH = 2;
  const DEBOUNCE = 220;

  const dialog = document.querySelector('[data-search-dialog]');
  if (!dialog) return;

  const input = dialog.querySelector('[data-search-input]');
  const results = dialog.querySelector('[data-search-results]');
  let timer = null;
  let controller = null;
  let active = -1;

  const items = () => [...results.querySelectorAll('[data-ps-item]')];

  const setActive = (next) => {
    const list = items();
    list.forEach((el) => el.classList.remove('is-active'));
    if (next < 0 || list.length === 0) {
      active = -1;
      input.removeAttribute('aria-activedescendant');
      return;
    }
    active = (next + list.length) % list.length;
    const el = list[active];
    el.classList.add('is-active');
    el.scrollIntoView({ block: 'nearest' });
  };

  const clear = () => {
    results.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    setActive(-1);
  };

  const search = async (term) => {
    // Abandon the in-flight request so slow responses can't overwrite newer ones.
    if (controller) controller.abort();
    controller = new AbortController();

    const url = new URL(`${window.Shopify.routes.root}search/suggest`, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', SECTION);
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[type]', 'product,collection,page,article');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Suggest failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('[data-ps-results]');
      results.innerHTML = next ? next.innerHTML : '';
      input.setAttribute('aria-expanded', results.innerHTML.trim() !== '' ? 'true' : 'false');
      setActive(-1);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Leave the form usable: Enter still submits to /search.
      console.error('[predictive-search]', error);
      clear();
    }
  };

  input.addEventListener('input', () => {
    const term = input.value.trim();
    clearTimeout(timer);

    if (term.length < MIN_LENGTH) {
      clear();
      return;
    }
    timer = setTimeout(() => search(term), DEBOUNCE);
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(active + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(active - 1);
    } else if (event.key === 'Enter' && active > -1) {
      // Only hijack Enter when a suggestion is highlighted; otherwise submit.
      event.preventDefault();
      items()[active].click();
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-search-open]')) {
      event.preventDefault();
      dialog.showModal();
      input.focus();
      return;
    }

    if (event.target.closest('[data-search-close]')) {
      dialog.close();
      return;
    }

    // Backdrop click.
    if (event.target === dialog) dialog.close();
  });

  dialog.addEventListener('close', () => {
    input.value = '';
    clear();
  });

  // Keyboard shortcut, matching the convention people expect from search.
  document.addEventListener('keydown', (event) => {
    if ((event.key === 'k' && (event.metaKey || event.ctrlKey)) || event.key === '/') {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || '');
      if (typing || dialog.open) return;
      event.preventDefault();
      dialog.showModal();
      input.focus();
    }
  });
})();
